import { type IntlShape, defineMessage, defineMessages } from 'react-intl'

export const inclusionsTitleStandardMessage = defineMessage({
  id: '63ExIlDQSv2Iwn27oHcu',
  defaultMessage: 'standard inclusions',
  description: 'Title for standard inclusions section in the offer inclusions section',
})

/**
 * Marketing change/update - with LuxPlus+ being introduced, bonus inclusions are now called "extra inclusions"
 * and LuxPlus+ inclusions are now referred to as "bonus inclusions" 😢
 */
export const inclusionsTitleBonusMessage = defineMessage({
  id: 'kdAiOssT3z2A09hpeS7r',
  defaultMessage: 'extra inclusions',
  description: 'Title for extra/bonus inclusions section in the inclusions list',
})

/**
 * Now referred to as "bonus inclusions"
 */
export const inclusionsTitleLuxPlusMessage = defineMessage({
  id: 'zUDZqM_sHYmB8iYqsCjq',
  defaultMessage: 'bonus inclusions',
  description: 'Title for LuxPlus bonus inclusions section in the inclusions list',
})

export function formatInclusionsTitleBonus(intl: IntlShape): string {
  return intl.formatMessage(inclusionsTitleBonusMessage)
}

export function formatInclusionsTitleLuxPlus(intl: IntlShape): string {
  return intl.formatMessage(inclusionsTitleLuxPlusMessage)
}

/**
 * Localised labels for svc-search inclusion filter types.
 * API matching values are derived from defaultMessage so filter boosting stays stable.
 */
export const inclusionFilterTypeMessages = defineMessages({
  default: {
    id: '_-2K7TIVVfwnAlMGnZx0',
    defaultMessage: 'Default',
    description: 'Inclusion filter category label for generic or default inclusions in hotel search filters',
  },
  breakfast: {
    id: 'QSJR5sPQ0XX8XpyaYJ39',
    defaultMessage: 'Breakfast',
    description: 'Inclusion filter category label for breakfast inclusions in hotel search filters',
  },
  allInclusiveDining: {
    id: 'q38XqSVLPPKQc3sbc1pB',
    defaultMessage: 'All-inclusive dining',
    description: 'Inclusion filter category label for all-inclusive dining in hotel search filters',
  },
  dailyDining: {
    id: 'dPeJboOsMOLuJVcqjZIl',
    defaultMessage: 'Daily dining',
    description: 'Inclusion filter category label for daily dining inclusions in hotel search filters',
  },
  snacks: {
    id: '9gUBXc4oo-Hr4RNvF1t1',
    defaultMessage: 'Snacks',
    description: 'Inclusion filter category label for snack inclusions in hotel search filters',
  },
  resortCredit: {
    id: '5NGmCwRqp2Z8JuD8GeQo',
    defaultMessage: 'Resort credit',
    description: 'Inclusion filter category label for resort credit inclusions in hotel search filters',
  },
  freeFlowDrinks: {
    id: 'JQO7IsWoW1QDYda8N1q4',
    defaultMessage: 'Free-flow drinks',
    description: 'Inclusion filter category label for free-flow drink inclusions in hotel search filters',
  },
  welcomeDrink: {
    id: 'KmOVmx1VmKEV5nFwrClj',
    defaultMessage: 'Welcome Drink',
    description: 'Inclusion filter category label for welcome drink inclusions in hotel search filters',
  },
  complementaryMinibar: {
    id: 'cRvTFMbCqVG5ENKat3_O',
    defaultMessage: 'Complementary minibar',
    description: 'Inclusion filter category label for complementary minibar inclusions in hotel search filters',
  },
  dailyDrink: {
    id: '2daB7Rs50z503ssAwias',
    defaultMessage: 'Daily drink',
    description: 'Inclusion filter category label for daily drink inclusions in hotel search filters',
  },
  roundtripDomesticFlights: {
    id: '8sc-7fsv8m59-lKdImd0',
    defaultMessage: 'Roundtrip Domestic Flights',
    description: 'Inclusion filter category label for roundtrip domestic flight inclusions in hotel search filters',
  },
  airportTransfer: {
    id: '9J2uwcz-e5vNrpFbxSV0',
    defaultMessage: 'Airport transfer',
    description: 'Inclusion filter category label for airport transfer inclusions in hotel search filters',
  },
  privateVipTransfer: {
    id: 'v6kFRBPf3yu1cHOMuLtk',
    defaultMessage: 'Private VIP Transfer',
    description: 'Inclusion filter category label for private VIP transfer inclusions in hotel search filters',
  },
  oneWayDomesticFlight: {
    id: 'DZGI6_DYWkvZ0ssNZbG5',
    defaultMessage: 'One-Way Domestic Flight',
    description: 'Inclusion filter category label for one-way domestic flight inclusions in hotel search filters',
  },
  spaTreatment: {
    id: 'KrOQiNgp-DLLDHlzyzcN',
    defaultMessage: 'Spa treatment',
    description: 'Inclusion filter category label for spa treatment inclusions in hotel search filters',
  },
  seatWithPlane: {
    id: 'llgU1MR30WKz12wNiXFL',
    defaultMessage: 'Seat with plane',
    description: 'Inclusion filter category label for seat upgrade inclusions in hotel search filters',
  },
  lateCheckoutEarlyCheckIn: {
    id: 'WIY2G4aHS98qnV1I-p3P',
    defaultMessage: 'Late checkout/early check-in',
    description: 'Inclusion filter category label for late checkout or early check-in inclusions in hotel search filters',
  },
  kidsStayFree: {
    id: 's69cmPd9CJ2ozxiUegHB',
    defaultMessage: 'Kids stay free',
    description: 'Inclusion filter category label for kids stay free inclusions in hotel search filters',
  },
  liftPass: {
    id: 'jZI7ADthEyXrOXYvj8nK',
    defaultMessage: 'Lift Pass',
    description: 'Inclusion filter category label for lift pass inclusions in hotel search filters',
  },
  shuttle: {
    id: 'CzUsYLim-QYaFqTpLXqM',
    defaultMessage: 'Shuttle',
    description: 'Inclusion filter category label for shuttle transfer inclusions in hotel search filters',
  },
  wifi: {
    id: '-1ALnf3TW523p0yYcdYD',
    defaultMessage: 'Wi-Fi',
    description: 'Inclusion filter category label for Wi-Fi inclusions in hotel search filters',
  },
  resortActivities: {
    id: 'ee0s02bk6OrjYPGNqws8',
    defaultMessage: 'Resort Activities',
    description: 'Inclusion filter category label for resort activity inclusions in hotel search filters',
  },
  golf: {
    id: '56BkGupVhFuvtszhbEoM',
    defaultMessage: 'Golf',
    description: 'Inclusion filter category label for golf inclusions in hotel search filters',
  },
  parking: {
    id: 'YEugPH9E8pkthzKPvDNI',
    defaultMessage: 'Parking',
    description: 'Inclusion filter category label for parking inclusions in hotel search filters',
  },
  loungeAccess: {
    id: 'E280h7gO2B--aoWok7nZ',
    defaultMessage: 'Lounge access',
    description: 'Inclusion filter category label for lounge access inclusions in hotel search filters',
  },
  taxesIncluded: {
    id: 'likkWCMj8q9jsjOcQp4l',
    defaultMessage: 'Taxes Included',
    description: 'Inclusion filter category label for tax-inclusive offers in hotel search filters',
  },
  discount: {
    id: 'eW-lrHNMvy4HpkWRIIpo',
    defaultMessage: 'Discount',
    description: 'Inclusion filter category label for discount inclusions in hotel search filters',
  },
  waivedResortFees: {
    id: 'drdcwy1HpSkXIkwRL6Y2',
    defaultMessage: 'Waived Resort Fees',
    description: 'Inclusion filter category label for waived resort fee inclusions in hotel search filters',
  },
})

/**
 * Stable svc-search inclusion filter values used for API and redux filter matching.
 * Values are sourced from message defaultMessage so they stay aligned with filter analytics.
 */
export const INCLUSION_FILTER_TYPE = {
  DEFAULT: inclusionFilterTypeMessages.default.defaultMessage,
  BREAKFAST: inclusionFilterTypeMessages.breakfast.defaultMessage,
  ALL_INCLUSIVE_DINING: inclusionFilterTypeMessages.allInclusiveDining.defaultMessage,
  DAILY_DINING: inclusionFilterTypeMessages.dailyDining.defaultMessage,
  SNACKS: inclusionFilterTypeMessages.snacks.defaultMessage,
  RESORT_CREDIT: inclusionFilterTypeMessages.resortCredit.defaultMessage,
  FREE_FLOW_DRINKS: inclusionFilterTypeMessages.freeFlowDrinks.defaultMessage,
  WELCOME_DRINK: inclusionFilterTypeMessages.welcomeDrink.defaultMessage,
  COMPLEMENTARY_MINIBAR: inclusionFilterTypeMessages.complementaryMinibar.defaultMessage,
  DAILY_DRINK: inclusionFilterTypeMessages.dailyDrink.defaultMessage,
  ROUNDTRIP_DOMESTIC_FLIGHTS: inclusionFilterTypeMessages.roundtripDomesticFlights.defaultMessage,
  AIRPORT_TRANSFER: inclusionFilterTypeMessages.airportTransfer.defaultMessage,
  PRIVATE_VIP_TRANSFER: inclusionFilterTypeMessages.privateVipTransfer.defaultMessage,
  ONE_WAY_DOMESTIC_FLIGHT: inclusionFilterTypeMessages.oneWayDomesticFlight.defaultMessage,
  SPA_TREATMENT: inclusionFilterTypeMessages.spaTreatment.defaultMessage,
  SEAT_WITH_PLANE: inclusionFilterTypeMessages.seatWithPlane.defaultMessage,
  LATE_CHECKOUT_EARLY_CHECK_IN: inclusionFilterTypeMessages.lateCheckoutEarlyCheckIn.defaultMessage,
  KIDS_STAY_FREE: inclusionFilterTypeMessages.kidsStayFree.defaultMessage,
  LIFT_PASS: inclusionFilterTypeMessages.liftPass.defaultMessage,
  SHUTTLE: inclusionFilterTypeMessages.shuttle.defaultMessage,
  WIFI: inclusionFilterTypeMessages.wifi.defaultMessage,
  RESORT_ACTIVITIES: inclusionFilterTypeMessages.resortActivities.defaultMessage,
  GOLF: inclusionFilterTypeMessages.golf.defaultMessage,
  PARKING: inclusionFilterTypeMessages.parking.defaultMessage,
  LOUNGE_ACCESS: inclusionFilterTypeMessages.loungeAccess.defaultMessage,
  TAXES_INCLUDED: inclusionFilterTypeMessages.taxesIncluded.defaultMessage,
  DISCOUNT: inclusionFilterTypeMessages.discount.defaultMessage,
  WAIVED_RESORT_FEES: inclusionFilterTypeMessages.waivedResortFees.defaultMessage,
} as const

export type InclusionFilterType = typeof INCLUSION_FILTER_TYPE[keyof typeof INCLUSION_FILTER_TYPE]

export const EXCLUDED_SEARCH_INCLUSION_FILTER_TYPES = new Set<InclusionFilterType>([
  INCLUSION_FILTER_TYPE.DEFAULT,
  INCLUSION_FILTER_TYPE.DISCOUNT,
])

export const inclusionFilterTypeMessageByValue = Object.values(inclusionFilterTypeMessages).reduce(
  (messages, message) => {
    messages[message.defaultMessage] = message
    return messages
  },
  {} as Record<InclusionFilterType, (typeof inclusionFilterTypeMessages)[keyof typeof inclusionFilterTypeMessages]>,
)

/**
 * Map: catalogue subcategory name -> svc-search inclusion filter type
 *
 * Prefer this over inclusionIconMap when tile/package inclusions include `category`
 * from public-offer (locale-stable). Falls back to inclusionIconMap[symbol] when
 * category is absent.
 */
export const inclusionCategoryMap: Partial<Record<string, InclusionFilterType>> = {
  'Generic Credit': INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  'LE Flight Credit': INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  'Spa Credit': INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  'Dining Credit': INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  'Daily Dining Credit': INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  'Nightly Cocktail': INCLUSION_FILTER_TYPE.DAILY_DRINK,
  'Free-Flow': INCLUSION_FILTER_TYPE.FREE_FLOW_DRINKS,
  'Free-Flow Hour': INCLUSION_FILTER_TYPE.FREE_FLOW_DRINKS,
  'Welcome Drink': INCLUSION_FILTER_TYPE.WELCOME_DRINK,
  'Welcome Bottle of Wine': INCLUSION_FILTER_TYPE.WELCOME_DRINK,
  Minibar: INCLUSION_FILTER_TYPE.COMPLEMENTARY_MINIBAR,
  Breakfast: INCLUSION_FILTER_TYPE.BREAKFAST,
  Lunch: INCLUSION_FILTER_TYPE.DAILY_DINING,
  Dinner: INCLUSION_FILTER_TYPE.DAILY_DINING,
  'All-Inclusive Dining': INCLUSION_FILTER_TYPE.ALL_INCLUSIVE_DINING,
  'Daily Dining': INCLUSION_FILTER_TYPE.DAILY_DINING,
  'Dining Experience': INCLUSION_FILTER_TYPE.DAILY_DINING,
  Snacks: INCLUSION_FILTER_TYPE.SNACKS,
  Discount: INCLUSION_FILTER_TYPE.DISCOUNT,
  'Waived Resort Fees': INCLUSION_FILTER_TYPE.WAIVED_RESORT_FEES,
  'Taxes Included': INCLUSION_FILTER_TYPE.TAXES_INCLUDED,
  'Club Lounge Access': INCLUSION_FILTER_TYPE.LOUNGE_ACCESS,
  'Seat with plane': INCLUSION_FILTER_TYPE.SEAT_WITH_PLANE,
  'Late Checkout / Early Check-In': INCLUSION_FILTER_TYPE.LATE_CHECKOUT_EARLY_CHECK_IN,
  'Guaranteed Late Checkout': INCLUSION_FILTER_TYPE.LATE_CHECKOUT_EARLY_CHECK_IN,
  'One Child Stays Free': INCLUSION_FILTER_TYPE.KIDS_STAY_FREE,
  'Multiple Children Stay Free': INCLUSION_FILTER_TYPE.KIDS_STAY_FREE,
  'Kids Club': INCLUSION_FILTER_TYPE.KIDS_STAY_FREE,
  'Lift Pass': INCLUSION_FILTER_TYPE.LIFT_PASS,
  Shuttle: INCLUSION_FILTER_TYPE.SHUTTLE,
  'Wi-Fi': INCLUSION_FILTER_TYPE.WIFI,
  'Resort Activities': INCLUSION_FILTER_TYPE.RESORT_ACTIVITIES,
  Golf: INCLUSION_FILTER_TYPE.GOLF,
  Parking: INCLUSION_FILTER_TYPE.PARKING,
  'Airport Transfer': INCLUSION_FILTER_TYPE.AIRPORT_TRANSFER,
  'Private VIP Transfer': INCLUSION_FILTER_TYPE.PRIVATE_VIP_TRANSFER,
  'Roundtrip Domestic Flights': INCLUSION_FILTER_TYPE.ROUNDTRIP_DOMESTIC_FLIGHTS,
  'One-Way Domestic Flight': INCLUSION_FILTER_TYPE.ONE_WAY_DOMESTIC_FLIGHT,
  'Spa Treatment': INCLUSION_FILTER_TYPE.SPA_TREATMENT,
  'Included Guests': INCLUSION_FILTER_TYPE.DEFAULT,
}

/**
 * Map: icon name -> svc-search inclusion filter type
 *
 * Fallback when tile inclusions lack `category` (legacy / sparse payloads).
 * Prefer inclusionCategoryMap when category is present.
 *
 * Context:
 * - svc-search uses standardised inclusion type names in filters (e.g., 'Resort credit', 'Airport transfer')
 * - Multiple inclusion categories map to the same filter value:
 *   - 'Generic Credit' (icon: plane_with_plus) → 'Resort credit'
 *   - 'Spa Credit' (icon: lotus_with_plus) → 'Resort credit'
 *   - 'Dining Credit' (icon: crossed_utensils_with_plus) → 'Resort credit'
 *   - 'Daily Dining Credit' (icon: dollar_sign_in_circle) → 'Resort credit'
 */

export const inclusionIconMap: Partial<Record<App.OfferFeatureSymbol, InclusionFilterType>> = {
  check: INCLUSION_FILTER_TYPE.DEFAULT,
  coffee: INCLUSION_FILTER_TYPE.BREAKFAST,
  coffee_cup: INCLUSION_FILTER_TYPE.BREAKFAST,
  cloche: INCLUSION_FILTER_TYPE.ALL_INCLUSIVE_DINING,
  crossed_utensils: INCLUSION_FILTER_TYPE.DAILY_DINING,
  apple: INCLUSION_FILTER_TYPE.SNACKS,
  plane_with_plus: INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  lotus_with_plus: INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  crossed_utensils_with_plus: INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  dollar_sign_in_circle: INCLUSION_FILTER_TYPE.RESORT_CREDIT,
  wine_glass_and_beer_glass: INCLUSION_FILTER_TYPE.FREE_FLOW_DRINKS,
  wine_glass_with_clock: INCLUSION_FILTER_TYPE.FREE_FLOW_DRINKS,
  wine_glass: INCLUSION_FILTER_TYPE.WELCOME_DRINK,
  drink_tray: INCLUSION_FILTER_TYPE.COMPLEMENTARY_MINIBAR,
  martini_glass: INCLUSION_FILTER_TYPE.DAILY_DRINK,
  plane_with_arrows: INCLUSION_FILTER_TYPE.ROUNDTRIP_DOMESTIC_FLIGHTS,
  taxi: INCLUSION_FILTER_TYPE.AIRPORT_TRANSFER,
  car: INCLUSION_FILTER_TYPE.PRIVATE_VIP_TRANSFER,
  plane_with_arrow: INCLUSION_FILTER_TYPE.ONE_WAY_DOMESTIC_FLIGHT,
  lotus: INCLUSION_FILTER_TYPE.SPA_TREATMENT,
  bed_with_upwards_arrow: INCLUSION_FILTER_TYPE.SEAT_WITH_PLANE,
  clock_with_plus: INCLUSION_FILTER_TYPE.LATE_CHECKOUT_EARLY_CHECK_IN,
  clock_with_check: INCLUSION_FILTER_TYPE.LATE_CHECKOUT_EARLY_CHECK_IN,
  childs_face: INCLUSION_FILTER_TYPE.KIDS_STAY_FREE,
  childs_face_with_plus: INCLUSION_FILTER_TYPE.KIDS_STAY_FREE,
  ski_lift: INCLUSION_FILTER_TYPE.LIFT_PASS,
  bus: INCLUSION_FILTER_TYPE.SHUTTLE,
  wifi_signal: INCLUSION_FILTER_TYPE.WIFI,
  tennis_ball: INCLUSION_FILTER_TYPE.RESORT_ACTIVITIES,
  golf: INCLUSION_FILTER_TYPE.GOLF,
  p_in_circle: INCLUSION_FILTER_TYPE.PARKING,
  seat_with_plane: INCLUSION_FILTER_TYPE.LOUNGE_ACCESS,
  receipt: INCLUSION_FILTER_TYPE.TAXES_INCLUDED,
  tag: INCLUSION_FILTER_TYPE.DISCOUNT,
  dollar_sign_in_circle_with_line_through: INCLUSION_FILTER_TYPE.WAIVED_RESORT_FEES,
}

export const inclusionFilterTypeIconMap = Object.entries(inclusionIconMap).reduce(
  (icons, [symbol, filterType]) => {
    if (filterType && !icons[filterType]) {
      icons[filterType] = symbol as App.OfferFeatureSymbol
    }
    return icons
  },
  {} as Partial<Record<InclusionFilterType, App.OfferFeatureSymbol>>,
)
